import React, {useState} from 'react';
import {Link} from "react-router-dom";

import CategoryTechnologies from '../../components/categoryTechnologies.js'
import CountryFlagAndName from '../../components/countryFlagAndName.js'
import DomainTable from '../../components/domainTable.js'
import GeoDistribution from '../../components/geoDistribution.js'
import TechnologyIconAndName from '../../components/technologyIconAndName.js'
import TechnologyMarketShare from '../../components/technologyChart.js'
import useGoogleCharts from '../../components/useGoogleCharts.js';
import DistributionByPopularityChart from '../../components/technologyDistributionChart.js'
import CountriesLeadList from '../../components/countriesLeadList.js'
import TLDLeadList from '../../components/tldLeadList.js'
import CopyLink from '../../components/copyLink.js'

import {_printNumber, _printPercent} from '../../helpers/helpers.js'

import {CategoryPageResult, TechnologySwitch as RtTechnologySwitch} from '../../../../server/routes/technology-type.js'

interface CategoryProps {
	server: string;
	pageData: unknown;
}

export default function Category({server, pageData}: CategoryProps) {

    const typedPageData = pageData as CategoryPageResult;
	const [data, setData] = useState(typedPageData);
	const google = useGoogleCharts();

	return (
		<>
            <section className="section section-sm">
                <div className="container">
                    <div className="row row-50">
                        <div className="col-md-8 col-lg-8 col-xl-8">
                            <CategoryLeftPanel props={data} google={google} />
                        </div>
                        <div className="col-md-4 col-lg-4 col-xl-4">
                            <CategoryRightPanel props={data} />
                        </div>
                    </div>
                    <CategoryBottomPanel props={data} google={google} />
                </div>
            </section>
		</>
	)
}

interface ComponentProps {
	props: CategoryPageResult;
}

interface ComponentWithGoogleProps {
	props: CategoryPageResult;
	google: wts_approved_any;
}

function CategoryLeftPanel({props, google}: ComponentWithGoogleProps) {
   return (<>

        <CategoryHeader props={props}/>
        <CategoryBrag props={props}/>
        <TechnologyMarketShare props={props}/>
        <CategoryUsageStats props={props}/>
        <DistributionByPopularityChart 
			subtitle ={`The correlation between utilization of ${props.CategoryShortName} technologies and the relative popularity of websites`}
            link={props.CanonicalURL} name={props.CategoryLongName} distribution={props.DistributionByPopularity} google={google}/>		

    </>);
}

function CategoryHeader({props}: ComponentProps) {
    return (<>
            <h2>
                <span className={`icon icon-xl text-primary mdi ${props.CategoryIcon}`}></span>
                &nbsp;{props.CategoryLongName}
            </h2>
            { props.ParentCategory &&
                <b>Parent Category:{' '}
                    <Link 
                        to={`/technology-type/${props.ParentCategory.CategoryURL}`}
                        className="link-hover" 
                        title={`${props.ParentCategory.CategoryShortName} category`} 
                    >
                        {props.ParentCategory.CategoryShortName} 
                    </Link>
                </b>
            }
            <p>
                {props.CategoryDescription}
            </p>
    </>)
}

function CategoryBrag({props}: ComponentProps) {

    if (!props.CategoryDomainsButton) return (<></>);

    return (<>
        <hr/>
        <br/>
        <p>
            We have discovered&nbsp; 
            <Link 
                to={props.CategoryDomainsButton.Href}
                className="link-hover" 
                title={`${props.CategoryDomainsButton.Title}`} 
                rel="noopener nofollow" 
            >
                <b>{_printNumber(props.CategoryDomainCount)} live websites</b>
            </Link>
            &nbsp;that use {props.CategoryShortName}.
        </p>
        <p>
            <Link 
                to={props.CategoryDomainsButton.Href}
                className="button button-secondary" 
                title={`${props.CategoryDomainsButton.Title}`} 
                rel="noopener nofollow" 
            >
                Download website list
            </Link>
        </p>
    </>)
}

function CategoryUsageStats({props}: ComponentProps) {
    return (<>
    
       <div className="row row-50">
            <div className="col-12">
                <h3 className="text-left" id='stats'>{props.CategoryLongName} usage statistics
                <CopyLink link={props.CanonicalURL}  anchor='stats'/> 
                </h3>
                <table className="table-custom table-custom-striped table-custom-primary">
                    <tbody>
                        <tr>
                            <td>Total sites that use {props.CategoryShortName}</td>
                            <td><span className="icon text-primary">
                                    <b>{_printNumber(props.CategoryUsage.DomainCount)}</b> websites
                            </span></td>
                        </tr>
                        <tr>
                            <td>Percentage of website that use category</td>
                            <td>
                                <span className="icon text-primary">
                                    <b>
                                        {_printPercent(props.CategoryUsage.Percentage)}%
                                    </b>
                                </span>
                            </td>
                        </tr>
                        <tr>
                            <td>Number of technologies in  {props.CategoryShortName} category</td>
                            <td>
                                <Link 
                                    to={`${props.CategoryURL}/technologies`}
                                    className="link-hover" 
                                    title={`${props.CategoryShortName} technolgies`} 
                                >
                                    <b>{props.NumberOFTechnologies}</b>
                                </Link>                                   
                            </td>
                        </tr>
                        <tr><td><hr/></td></tr>
                        <tr>
                            <td>Category leader</td>
                            <td>
                                <b>
                                    <TechnologyIconAndName 
                                        technology={props.CategoryLeader.TechnologyShortName}
                                        technologyURL={props.CategoryLeader.TechnologyURL}
                                        technologyIcon={props.CategoryLeader.TechnologyIcon}
                                    />
                                </b>                                
                            </td>
                        </tr>   
                        <tr>
                            <td>Fastest Growing Technology</td>
                            <td>
                                <b>
                                    <TechnologyIconAndName 
                                        technology={props.FastestGrowingTechnology.TechnologyShortName}
                                        technologyURL={props.FastestGrowingTechnology.TechnologyURL}
                                        technologyIcon={props.FastestGrowingTechnology.TechnologyIcon}
                                    />
                                </b>
                            </td>
                        </tr>   
                        <tr>
                            <td>Most Popular in Country</td>
                            <td>
                                <b>
                                    <CountryFlagAndName countryCode={props.MostPopularInCountry} />
                                </b>
                            </td>
                        </tr>   
                    </tbody>
                </table>
            </div>
        </div>   
    </>);
}

function CategoryInfo({props}: ComponentProps) {

    return (<>
    
    <article className="box-bordered">
        <h4>Description</h4>
        <ul className="list-marked">
            <li>Providers Discovered -&nbsp;    
                <b>
                    <Link
                        to={`${props.CategoryURL}/technologies`}
                        className="link-hover" 
                        title={`${props.CategoryLongName} technologies`} 
                    >
                        {props.NumberOFTechnologies}
                    </Link>
                </b>               
            </li>
            {
                props.CategoryWiki &&
                <li>Wikipedia -&nbsp; 
                    <b>
                        <a
                            href={props.CategoryWiki}
                            className="link-hover" 
                            rel="noopener nofollow" 
                            title={`${props.CategoryLongName}  Wikipedia page`} 
                            target="_blank" 
                        >
                            {props.CategoryShortName}&nbsp;&nbsp;&nbsp;&nbsp;
                            <span style={{color:'black'}} className="icon text-right fa fa-external_link"/>	
                        </a>
                    </b>
                </li>
            }
        </ul>
    </article>

    </>)
}

function AltCategoryNames({props}: ComponentProps) {

    if (!props.CategoryAltNames || props.CategoryAltNames.length === 0) return (<></>);

    return (<>

        <br/>
        <article className="box-bordered">
            <h4>Alternative Names</h4>
            <ul className="list-marked">
                {
                    props.CategoryAltNames.map((name, indexKey) => {
                        return (
                            <li  key={indexKey}>
                                {name}
                            </li>
                        )
                    })
                }
            </ul>
        </article>
    
    </>)

}

function Subcategories({props}: ComponentProps) {

    if (!props.Subcategories) return (<></>)

    return (<>

        <br/>
        <article className="box-bordered">
            <h4>Subcategories</h4>
            <ul className="list-marked">
                {
                    props.Subcategories.map((subcategory, indexKey) => {
                        return (             
                            <li  key={indexKey}>
                                <b>
                                    <Link 
                                        to={`/technology-type/${subcategory.CategoryURL}`}
                                        className="link-hover" 
                                        rel="noopener nofollow" 
                                        title={subcategory.CategoryLongName} 
                                    >
                                        {subcategory.CategoryShortName}
                                    </Link>
                                </b>
                            </li>
                            )
                    })
                }
            </ul>
        </article>    
    
    </>)
}

function RelatedCategories({props}: ComponentProps) {

    if (!props.RelatedCategories) return (<></>);

    return (<>

        <br/>
        <article className="box-bordered">
            <h4>Related Categories</h4>
            <ul className="list-marked">
                {
                    props.RelatedCategories.map((relatedCategory, indexKey) => {

                        return (                    
                            <li key={indexKey}>
                                <b>
                                    <Link 
                                        to={`/technology-type/${relatedCategory.CategoryURL}`}
                                        className="link-hover" 
                                        rel="noopener nofollow" 
                                        title={relatedCategory.CategoryLongName} 
                                    >
                                        {relatedCategory.CategoryShortName}
                                    </Link>
                                </b>
                            </li>
                        )
                    })
                }
            </ul>
        </article>

    </>)
}

function CategoryRightPanel({props}: ComponentProps) {
    return (<>

        <CategoryInfo props={props} />
        <AltCategoryNames props={props}/>
        <Subcategories props={props} />
        <RelatedCategories props={props}/>
        <CountriesLeadList report={props.CategoryMoniker} name={props.CategoryShortName} reporttype='category' geoData={props.CategoryGeoData} />
		<TLDLeadList report={props.CategoryMoniker} name={props.CategoryShortName} reporttype='category' tlds={props.TLDs}/>		 
    </>);
}

interface TechnologySwitchProps {
	technologySwitch: RtTechnologySwitch;
}

function TechnologySwitch({technologySwitch}: TechnologySwitchProps) {
    return (

        <tr>
            <td>
                <b>
                    <TechnologyIconAndName 
                        technology={technologySwitch.TechnologyFrom.TechnologyShortName}
                        technologyURL={technologySwitch.TechnologyFrom.TechnologyURL}
                        technologyIcon={technologySwitch.TechnologyFrom.TechnologyIcon}
                    />
                </b>
            </td>
            <td>
                <b>
                    <TechnologyIconAndName 
                        technology={technologySwitch.TechnologyTo.TechnologyShortName}
                        technologyURL={technologySwitch.TechnologyTo.TechnologyURL}
                        technologyIcon={technologySwitch.TechnologyTo.TechnologyIcon}
                    />
                </b>
            </td>
            <td>
                {_printNumber(technologySwitch.DomainCount)}
            </td>
        </tr>

    )
}

function TopCategorySwitches({props}: ComponentProps) {
    
    if (!props.IsExclusive || !props.TechnologySwitches || !props.TechnologySwitches.length) {
        return (<></>);
    }

    return (<>
    
        <div className="row row-50">
            <div className="col-12">  
                <h3 className="text-left">Top technology switches</h3>
                Number of websites that recently switched technologies within category
                <br />
                <br />
                <table className="table-custom table-custom-striped table-custom-primary">
                    <thead>
                        <tr>
                            <th>
                                From
                            </th>
                            <th>
                                To
                            </th>
                            <th>
                                Website Count
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            props.TechnologySwitches.map((technologySwitch, indexKey) => {
                                return (<TechnologySwitch technologySwitch={technologySwitch} key={indexKey}/>)
                            })
                        }
                    </tbody>
                </table>                                      
            </div>
        </div>    
    
    </>)
}

function CategoryBottomPanel({props, google}: ComponentWithGoogleProps) {

    return (<>
        <CategoryTechnologies categoryTechnologies={props.CategoryTechnologies} link={props.CanonicalURL}/>
        <TopCategorySwitches props={props}/>
        <GeoDistribution link={props.CanonicalURL} geoData={props.CategoryGeoData} google={google}/>
        <DomainTable domainList={props.CategoryDomains}  link={props.CanonicalURL}/>
    </>);
}

function LeadListsTable({props}: ComponentProps) {

	if (!props) {
		return null
	}
	const name = props.CategoryLongName

	return (
		<>	
			<br/>
			<hr></hr>		
			<br/>
			<h4>Lead Lists ideas</h4>
			Download targeted list of live websites based on changes in usage of {name} technology 
			{props.IsExclusive ? ` or its competitors from ${props.CategoryShortName}` : ''}

			<table className="table-custom table-custom-striped table-custom-primary">
				<tbody>
					<LeadList props={props} title={`Websites that started using ${name} in the last 3 month`} count={`2,462`}/>
					<LeadList props={props} title={`Websites that stopped using ${name} in the last 3 month`} count={`1,345`}/>
					<LeadList props={props} title={`Websites that do not use any ${name} technologies but used it in the past.`} count={`462`}/>
					<LeadList props={props} title={`Websites that started using ${name} 9-12 month ago`} count={`354`}/>
				</tbody>
			</table>

		</>
	)
}

interface LeadListProps {
	props: CategoryPageResult;
	title: string;
	count: string;
}

function LeadList({props, title, count}: LeadListProps) {

	if (!title || !count) {
		return null
	}
	return (
		<>	
			<tr>
				<td>{title}&nbsp;</td>
				<td align="right">
					<Link 
						className="link-hover word-break" 
						title={`Download Lead List`}									
						to = {`/create-report?category=${props.CategoryMoniker}`}
					><b>{count}</b> websites 
					</Link>						
				</td>
			</tr>
		</>
	)
}